import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DisplayStatus, GradeRange, OrderByOption, ProjectTab, SortDirection } from '../types'

const DEFAULT_TAB: ProjectTab = 'my'
const DEFAULT_STATUS: DisplayStatus = 'all'
const DEFAULT_ORDER_BY: OrderByOption = 'created_at'
const DEFAULT_DIRECTION: SortDirection = 'desc'
const DEFAULT_PAGE = 1
const MAX_SEARCH_LENGTH = 100

interface UseProjectsQueryParamsReturn {
  tab: ProjectTab
  status: DisplayStatus
  search: string
  orderBy: OrderByOption
  direction: SortDirection
  page: number
  gradeId: string
  gradeRange: GradeRange
  categoryId: string
  setTab: (tab: ProjectTab) => void
  setStatus: (status: DisplayStatus) => void
  setSearch: (search: string) => void
  setOrderBy: (orderBy: OrderByOption, direction?: SortDirection) => void
  setPage: (page: number) => void
  setGradeId: (gradeId: string) => void
  setGradeRange: (range: GradeRange) => void
  setCategoryId: (id: string) => void
  resetFilters: () => void
}

const isValidGradeRange = (value: string | null): value is GradeRange =>
  value === 'all' || value === 'k-2' || value === '3-5' || value === '6-8' || value === '9-12'

const isValidTab = (value: string | null): value is ProjectTab =>
  value === 'my' || value === 'library'

const isValidStatus = (value: string | null): value is DisplayStatus =>
  value === 'all' || value === 'draft' || value === 'assigned' || value === 'completed'

const isValidOrderBy = (value: string | null): value is OrderByOption =>
  value === 'name' || value === 'status' || value === 'created_at'

const isValidDirection = (value: string | null): value is SortDirection =>
  value === 'asc' || value === 'desc'

const parsePageNumber = (value: string | null): number => {
  if (!value) return DEFAULT_PAGE
  const parsed = parseInt(value, 10)
  return isNaN(parsed) || parsed < 1 ? DEFAULT_PAGE : parsed
}

export const useProjectsQueryParams = (): UseProjectsQueryParamsReturn => {
  const [searchParams, setSearchParams] = useSearchParams()

  const tab = useMemo((): ProjectTab => {
    const value = searchParams.get('tab')
    return isValidTab(value) ? value : DEFAULT_TAB
  }, [searchParams])

  const status = useMemo((): DisplayStatus => {
    const value = searchParams.get('status')
    return isValidStatus(value) ? value : DEFAULT_STATUS
  }, [searchParams])

  const search = useMemo((): string => {
    const value = searchParams.get('search') || ''
    return value.slice(0, MAX_SEARCH_LENGTH)
  }, [searchParams])

  const orderBy = useMemo((): OrderByOption => {
    const value = searchParams.get('order_by')
    return isValidOrderBy(value) ? value : DEFAULT_ORDER_BY
  }, [searchParams])

  const direction = useMemo((): SortDirection => {
    const value = searchParams.get('direction')
    return isValidDirection(value) ? value : DEFAULT_DIRECTION
  }, [searchParams])

  const page = useMemo((): number => {
    return parsePageNumber(searchParams.get('page'))
  }, [searchParams])

  const gradeId = useMemo((): string => {
    return searchParams.get('grade_id') || ''
  }, [searchParams])

  const gradeRange = useMemo((): GradeRange => {
    const value = searchParams.get('grade_range')
    return isValidGradeRange(value) ? value : 'all'
  }, [searchParams])

  const categoryId = useMemo((): string => {
    return searchParams.get('category_id') || ''
  }, [searchParams])

  const setTab = useCallback((newTab: ProjectTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', newTab)
      next.delete('status')
      next.delete('search')
      next.delete('page')
      next.delete('grade_id')
      next.delete('grade_range')
      next.delete('category_id')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setStatus = useCallback((newStatus: DisplayStatus) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newStatus === 'all') {
        next.delete('status')
      } else {
        next.set('status', newStatus)
      }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setSearch = useCallback((newSearch: string) => {
    const trimmed = newSearch.slice(0, MAX_SEARCH_LENGTH)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (trimmed) {
        next.set('search', trimmed)
      } else {
        next.delete('search')
      }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setOrderBy = useCallback((
    newOrderBy: OrderByOption,
    newDirection?: SortDirection,
  ) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('order_by', newOrderBy)
      if (newDirection) {
        next.set('direction', newDirection)
      }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setPage = useCallback((newPage: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newPage <= 1) {
        next.delete('page')
      } else {
        next.set('page', String(newPage))
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setGradeRange = useCallback((newRange: GradeRange) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newRange === 'all') {
        next.delete('grade_range')
      } else {
        next.set('grade_range', newRange)
      }
      next.delete('grade_id')
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setCategoryId = useCallback((newCategoryId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newCategoryId) {
        next.set('category_id', newCategoryId)
      } else {
        next.delete('category_id')
      }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setGradeId = useCallback((newGradeId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newGradeId) {
        next.set('grade_id', newGradeId)
      } else {
        next.delete('grade_id')
      }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const resetFilters = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams()
      const currentTab = prev.get('tab')
      if (currentTab && isValidTab(currentTab)) {
        next.set('tab', currentTab)
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  return {
    tab,
    status,
    search,
    orderBy,
    direction,
    page,
    gradeId,
    gradeRange,
    categoryId,
    setTab,
    setStatus,
    setSearch,
    setOrderBy,
    setPage,
    setGradeId,
    setGradeRange,
    setCategoryId,
    resetFilters,
  }
}
